import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { List, ListItem, ListItemText } from '@material-ui/core';
import { ChevronRightRounded } from '@material-ui/icons';
import { useStoreActions, useStoreState } from 'easy-peasy';
import pick from 'lodash/pick';
import _e from 'common/localeUtil';
import {
	changeTemplate,
	getEntityWebsiteReportEbook,
	getFullSubdomainURL,
	unPublishTemplate
} from '../utils';

import EditorLeftPanelContent from './EditorLeftPanelContent';
import { BRANDING_TYPE } from '../constants';
import { ConfirmActionDialog } from './dialog';
import OtherSettingsDialog from './dialog/OtherSettingsDialog';
import EditDisclaimersDialog from './dialog/EditDisclaimersDialog';

const useStyles = makeStyles(theme => ({
	listItemStyle: {
		backgroundColor: theme.palette.primary[400],
		// opacity: 0.6,
		borderRadius: 4,
		cursor: 'pointer',
		transition: 'background 0.5s',
		'&:hover': {
			// zIndex: 0,
			backgroundColor: theme.palette.primary[300]
			// opacity: 0.8
		}
	},
	leftPanelPreview: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(1),
		paddingLeft: theme.spacing(4),
		paddingRight: theme.spacing(4)
	},
	wrapper: {
		width: '100%',
		fontSize: 15,
		fontWeight: 'bold',
		'& i': {
			marginRight: 10
		}
	},
	previewButtons: {
		textAlign: 'center'
	}
}));

const EditorLeftPanelResetTemplate = () => {
	const classes = useStyles();
	const template = useStoreState(state => state.template);
	const loadTemplate = useStoreActions(actions => actions.template.loadTemplate);
	const [loading, setLoading] = useState(false);
	const [showConfirmNewTemplateDialog, setShowConfirmNewTemplateDialog] = useState(false);
	const [showConfirmResetTemplateDialog, setShowConfirmResetTemplateDialog] = useState(false);
	const [showConfirmUnpublishDialog, setShowConfirmUnpublishDialog] = useState(false);

	const [showOtherSettingsDialog, setShowOtherSettingsDialog] = useState(false);
	const [showEditDisclaimersDialog, setShowEditDisclaimersDialog] = useState(false);
	const subdomain = useStoreState(state => state.template.subdomain);
	const setEbook = useStoreActions(actions => actions.template.setEbook);

	const fullWebsiteURL = getFullSubdomainURL(subdomain);

	const toggleShowEditDisclaimersDialog = () => {
		window.gm_track(`Settings: Disclaimers clicked`);

		setShowEditDisclaimersDialog(!showEditDisclaimersDialog);
	};

	const toggleShowOtherSettingsDialog = () => {
		window.gm_track(`Settings: Other Settings clicked`);

		setShowOtherSettingsDialog(!showOtherSettingsDialog);
	};

	const toggleShowConfirmResetTemplateDialog = () => {
		window.gm_track(`Settings: Reset template clicked`);

		setShowConfirmResetTemplateDialog(!showConfirmResetTemplateDialog);
	};

	const toggleShowConfirmNewTemplateDialog = () => {
		window.gm_track(`Settings: Choose new template clicked`);

		setShowConfirmNewTemplateDialog(!showConfirmNewTemplateDialog);
	};

	const toggleShowConfirmUnpublishDialog = () => {
		window.gm_track(`Settings: Unpublish clicked`);

		setShowConfirmUnpublishDialog(!showConfirmUnpublishDialog);
	};

	const resetTemplate = async () => {
		window.gm_track(`Settings: Confirmed reset template`);
		const adviserInfo = pick(template, 'adviserData', 'contactForm', 'extraProfile');
		setLoading(true);
		await changeTemplate(template.template, { forceReset: true }, adviserInfo);
		await loadTemplate();
		setLoading(false);
		toggleShowConfirmResetTemplateDialog();
	};

	const handleUnpublish = async () => {
		window.gm_track(`Settings: Unpublished website`);
		toggleShowConfirmUnpublishDialog();

		// setLoading(true);
		setEbook({ final_s3_url: 'Generating the final eBook PDF, please wait ...' });
		const publishResult = await unPublishTemplate(template.template);
		setEbook({ final_s3_url: publishResult.final_s3_url });

		await loadTemplate();
		// setLoading(false);
	};

	const handleChooseNewTemplate = () => {
		window.gm_track(`Settings: Confirmed choose new template`);

		toggleShowConfirmNewTemplateDialog();
		let brandingName = 'website';
		if (window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_REPORT_COVER) {
			brandingName = 'report';
		} else if (window.GM_BRANDING_TYPE === BRANDING_TYPE.TYPE_EBOOK_INTRO) {
			brandingName = 'ebook';
		}
		const url = `${process.env.REACT_APP_BASE_API_URL}/branding/${brandingName}`;
		window.open(url, '_self');
	};

	const renderListItem = (onClickCallBack, listItemText, divider = true) => {
		return (
			<ListItem
				onClick={onClickCallBack}
				classes={{ root: classes.listItemStyle }}
				divider={divider}
			>
				<ListItemText>{listItemText}</ListItemText>
				<ChevronRightRounded />
			</ListItem>
		);
	};

	const entity = getEntityWebsiteReportEbook();
	const entityCover = getEntityWebsiteReportEbook(true);

	const content = (
		<div id="left-panel-reset">
			<div className={`${classes.wrapper}`}>
				<List>
					{renderListItem(toggleShowConfirmNewTemplateDialog, _e('choose_new_template'))}
					{renderListItem(toggleShowConfirmResetTemplateDialog, _e('reset_template'))}

					{!window.GM_BRANDING_TYPE_REPORT_EBOOK && (
						<>
							{renderListItem(toggleShowEditDisclaimersDialog, _e('disclaimers'))}
							{renderListItem(toggleShowOtherSettingsDialog, _e('other_settings'))}
						</>
					)}
					{renderListItem(toggleShowConfirmUnpublishDialog, _e('unpublish'))}
				</List>
			</div>

			{showConfirmResetTemplateDialog && (
				<ConfirmActionDialog
					message={`${_e('reset_template_confirm_msg')} ${
						window.GM_BRANDING_TYPE_REPORT_EBOOK
							? _e('ebook_template_all_types', { entity })
							: ''
					}`}
					confirmLabel={_e('yes_reset_now')}
					loading={loading}
					open={showConfirmResetTemplateDialog}
					handleClose={toggleShowConfirmResetTemplateDialog}
					handleOK={resetTemplate}
				/>
			)}
			{showConfirmNewTemplateDialog && (
				<ConfirmActionDialog
					message={_e('are_you_sure_desc')}
					open={showConfirmNewTemplateDialog}
					confirmLabel={`${_e('yes')} ${_e('change_now')}`}
					loading={loading}
					handleClose={toggleShowConfirmNewTemplateDialog}
					handleOK={handleChooseNewTemplate}
				/>
			)}
			{showConfirmUnpublishDialog && (
				<ConfirmActionDialog
					title={_e('unpublish_your_entitycover', { entityCover })}
					message={
						window.GM_BRANDING_TYPE_REPORT_EBOOK ? (
							<div>{_e('ebook_unpublish_message', { entityCover, html: true })}</div>
						) : (
							<div>{_e('unpublish_website_msg', { fullWebsiteURL })}</div>
						)
					}
					open={showConfirmUnpublishDialog}
					confirmLabel={_e('yes_unpublish_now')}
					loading={loading}
					handleClose={toggleShowConfirmUnpublishDialog}
					handleOK={handleUnpublish}
				/>
			)}
			{showOtherSettingsDialog && (
				<OtherSettingsDialog
					open={showOtherSettingsDialog}
					handleClose={toggleShowOtherSettingsDialog}
				/>
			)}
			{showEditDisclaimersDialog && (
				<EditDisclaimersDialog
					open={showEditDisclaimersDialog}
					handleClose={toggleShowEditDisclaimersDialog}
				/>
			)}
		</div>
	);

	return (
		<EditorLeftPanelContent
			title={_e('settings')}
			subtitle={_e('settings_desc')}
			content={content}
		/>
	);
};

EditorLeftPanelResetTemplate.propTypes = {
	// openLinkedInDialog: PropTypes.bool.isRequired,
	// setOpenLinkedInDialog: PropTypes.func.isRequired
};

export default EditorLeftPanelResetTemplate;
